import React, { Context, createContext } from 'react';
import { styled } from '@mui/material/styles';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import Box from '@mui/material/Box';
import MuiAccordionSummary, {
    AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import { OpenAPIV3_1 as OpenAPI } from "openapi-types";
import _, { Dictionary } from 'lodash';
import Methods from './components/Methods';
import { isResourceAllowed } from 'src/auth';

export const ResourceContext: Context<string | null> = createContext<string | null>(null);

const extractResource = (pathKey: string) => {
    const arr = /\/([^/]+).*/.exec(pathKey);
    return arr![1];
}

const groupByResource: (pathsObj: OpenAPI.PathsObject) => Dictionary<OpenAPI.PathsObject> = (pathsObj) => {
    const groupped: Dictionary<OpenAPI.PathsObject> = {};
    _.keys(pathsObj).forEach(pathKey => {
        const pathItemObj: OpenAPI.PathItemObject = pathsObj[pathKey]!;

        const resource = extractResource(pathKey);
        let group: OpenAPI.PathsObject = groupped[resource];
        if (group === undefined) {
            group = {};
            groupped[resource] = group;
        }

        group[pathKey] = pathItemObj;
    });
    return groupped;
}

export interface ResourcesProps {
    json: OpenAPI.Document;
}

const Accordion = styled((props: AccordionProps) => (
    <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    '&:not(:last-child)': {
        borderBottom: 0,
    },
    '&:before': {
        display: 'none',
    },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary
        expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
        {...props}
    />
))(({ theme }) => ({
    backgroundColor:
        theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, .05)'
            : 'rgba(0, 0, 0, .03)',
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
        transform: 'rotate(90deg)',
    },
    '& .MuiAccordionSummary-content': {
        marginLeft: theme.spacing(1),
    },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

const Resources: React.FC<ResourcesProps> = ({ json }) => {
    const resources = groupByResource(json.paths!);
    const [expanded, setExpanded] = React.useState<string | false>('panel1');

    const handleChange =
        (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
            setExpanded(newExpanded ? panel : false);
        };

    return (
        <Box>
            {
                _.keys(resources).filter(resource => isResourceAllowed(resource)).map((resource, index) => {
                    const pathsObj: OpenAPI.PathsObject = resources[resource];

                    return (
                        <ResourceContext.Provider value={resource} key={index}>
                            <Accordion
                                TransitionProps={{ unmountOnExit: true }}
                                key={index}
                                expanded={expanded === resource}
                                onChange={handleChange(resource)}
                            >
                                <AccordionSummary>
                                    <Typography sx={{ fontWeight: 600 }}>{resource}</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {
                                        _.keys(pathsObj).map((key, index) =>
                                            <Methods
                                                key={index}
                                                pathKey={key}
                                                pathItemObject={pathsObj[key]!}
                                            />
                                        )
                                    }
                                </AccordionDetails>
                            </Accordion>
                        </ResourceContext.Provider>
                    )
                })
            }

        </Box>
    );
}

export default Resources;
